<template>
  <div class="page work-page">
    <section class="page-hero section-wrap work-hero">
      <div>
        <p class="section-kicker">SELECTED WORK · PRODUCTS · EXPERIMENTS</p>
        <h1>Things I&rsquo;ve made<br /><em>move, think, and work.</em></h1>
      </div>
      <p class="page-hero-aside">
        From production platforms serving millions to weekend experiments that
        began with “what if?”—a living cabinet of shipped curiosity.
      </p>
    </section>

    <section class="featured-project section-wrap">
      <div class="featured-project-copy">
        <div class="story-meta"><span>FEATURED IMPACT</span><span>META · 2025—NOW</span></div>
        <h2>Ranking Engineer Agent</h2>
        <p class="project-lede">An autonomous AI agent accelerating iteration across Meta&rsquo;s Ads ranking systems.</p>
        <p>
          I work on automating the path from an engineer&rsquo;s idea to a model
          experiment—compressing complex infrastructure workflows into an agentic
          system that can reason, act, and help teams learn faster.
        </p>
        <div class="tag-list"><span>Agentic systems</span><span>ML infrastructure</span><span>Ads ranking</span></div>
      </div>
      <div class="agent-console" aria-label="Abstract agent workflow visualization">
        <div class="console-bar"><i></i><i></i><i></i><span>REA / LIVE SYSTEM</span></div>
        <div class="console-flow">
          <div><small>01</small><strong>IDEA</strong><span>Engineer intent</span></div>
          <b>→</b>
          <div><small>02</small><strong>AGENT</strong><span>Plan + execute</span></div>
          <b>→</b>
          <div><small>03</small><strong>LEARN</strong><span>Ranking signal</span></div>
        </div>
        <div class="console-pulse"><span></span> ITERATION LOOP ACTIVE</div>
      </div>
    </section>

    <section class="project-archive section-wrap">
      <div class="archive-head">
        <div><p class="section-kicker">THE BUILD LOG</p><h2>Selected projects.</h2></div>
        <p>Commercial systems, hackathon swings, and open-source tools.</p>
      </div>

      <div class="project-grid">
        <a
          v-for="project in projects"
          :key="project.title"
          :href="project.url"
          target="_blank"
          rel="noopener noreferrer"
          :class="['project-card', { 'project-wide': project.wide }]"
        >
          <div class="project-visual">
            <div class="project-type-art" aria-hidden="true">
              <small>{{ project.code }}</small>
              <span>{{ project.art }}</span>
              <i>{{ project.signal }}</i>
            </div>
            <span class="project-arrow">↗</span>
          </div>
          <div class="project-card-copy">
            <div class="story-meta"><span>{{ project.kind }}</span><span>{{ project.year }}</span></div>
            <h3>{{ project.title }}</h3>
            <p>{{ project.description }}</p>
            <div class="tag-list"><span v-for="tag in project.tags" :key="tag">{{ tag }}</span></div>
          </div>
        </a>
      </div>
    </section>

    <section class="experiment-strip section-wrap">
      <p class="section-kicker">ALSO IN THE LAB</p>
      <div class="experiment-list">
        <a v-for="item in experiments" :key="item.name" :href="item.url" target="_blank" rel="noopener noreferrer">
          <span>{{ item.name }}</span><small>{{ item.note }}</small><b>↗</b>
        </a>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  data() {
    return {
      projects: [
        {
          title: "Brembo GenAI Hackathon",
          kind: "GRAND PRIZE · $10K",
          year: "2023",
          art: "BRAKE / AI",
          code: "BREMBO · 01",
          signal: "FORMULATE → SIMULATE → SCORE",
          description: "Generated practical brake-pad formulations and performance metrics using generative AI plus classical ML.",
          tags: ["GenAI", "Machine learning", "Materials"],
          url: "https://www.linkedin.com/feed/update/urn:li:activity:7125907704966967297",
          wide: true,
        },
        {
          title: "FlowType",
          kind: "NATIVE MACOS · VOICE AI",
          year: "2026",
          art: "VOICE / FLOW",
          code: "MACOS · 02",
          signal: "HOLD → SPEAK → TYPE",
          description: "A privacy-first push-to-talk dictation app that runs WhisperKit on-device and types directly into the focused field.",
          tags: ["Swift", "WhisperKit", "On-device ML"],
          url: "https://github.com/CodHeK/FlowType",
          wide: true,
        },
        {
          title: "Dashboard Migration",
          kind: "SUMO LOGIC · PLATFORM",
          year: "2022—24",
          art: "DASH / V2",
          code: "SUMO · 03",
          signal: "MILLIONS OF DASHBOARDS → ONE PATH",
          description: "A one-click migration experience designed to transition millions of classic dashboards to the new observability platform.",
          tags: ["React", "TypeScript", "Observability"],
          url: "https://help.sumologic.com/docs/dashboards/about/",
        },
        {
          title: "OpenCurriculum",
          kind: "F8 HACKATHON · TOP 8",
          year: "2019",
          art: "OPEN / EDU",
          code: "F8 · 04",
          signal: "TEACHERS ↔ KNOWLEDGE ↔ REGIONS",
          description: "A global collaboration platform for educators to curate regionally aligned learning resources.",
          tags: ["Education", "Product", "Hackathon"],
          url: "https://devpost.com/software/opencurriculum-by-wattba",
        },
        {
          title: "Voila.js",
          kind: "OPEN SOURCE · FRAMEWORK",
          year: "2019",
          art: "VOILA / JS",
          code: "OSS · 05",
          signal: "MARKUP + MODULES + LOGIC",
          description: "A small front-end framework for bringing modular structure and logic to static HTML.",
          tags: ["JavaScript", "Framework", "DX"],
          url: "https://github.com/CodHeK/Voila",
          wide: true,
        },
        {
          title: "PYSOC.JS",
          kind: "OPEN SOURCE · DATA",
          year: "2018",
          art: "GSOC / DATA",
          code: "OSS · 06",
          signal: "SEARCH → FILTER → DISCOVER",
          description: "An interactive GSoC explorer with fuzzy search, instant filtering, and useful organization insights.",
          tags: ["React", "Python", "Search"],
          url: "https://github.com/CodHeK/pysoc.js",
        },
        {
          title: "XRayDOM",
          kind: "BROWSER TOOL",
          year: "2018",
          art: "DOM / XRAY",
          code: "LAB · 07",
          signal: "POINT → INSPECT → UNDERSTAND",
          description: "Inspect the DOM directly under your cursor—a playful alternative to opening developer tools.",
          tags: ["JavaScript", "DOM", "Tooling"],
          url: "https://github.com/CodHeK/XrayDOM",
        },
      ],
      experiments: [
        { name: "qsearch", note: "React search powered by a Trie", url: "https://github.com/CodHeK/qsearch" },
        { name: "mitiBot", note: "Graph ML for bot mitigation", url: "https://github.com/CodHeK/mitiBot" },
        { name: "project-Xplore", note: "Deep chained synonym trees", url: "https://github.com/CodHeK/project-Xplore" },
        { name: "Heatmap-Sketcher", note: "GitHub heatmap graffiti", url: "https://github.com/CodHeK/Heatmap-Sketcher" },
      ],
    };
  },
};
</script>
